import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { clinicAppointmentsAPI } from "../../services/api";
import { pdfServices } from "../../services/pdfservices";
import InpatientDetailsModal from "../../components/modals/InpatientDetailsModal";
import OutpatientDetailsOverlay from "../../components/reception/OutpatientDetailsOverlay";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import "../../styles/visit-reports.css";

const VisitReports = ({ clinicData }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const clinicId = clinicData?.clinic?.id;
  useFlatpickrUserType();

  const [visits, setVisits] = useState([]);
  const [allVisits, setAllVisits] = useState([]); // Store all visits for filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(20);
  const [selectedInpatientId, setSelectedInpatientId] = useState(null);
  const [selectedOutpatientId, setSelectedOutpatientId] = useState(null);
  const [showInpatientModal, setShowInpatientModal] = useState(false);
  const [showOutpatientModal, setShowOutpatientModal] = useState(false);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);
  const [generatingPrescriptionId, setGeneratingPrescriptionId] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    guardianName: "",
    guardianPhone: "",
    petName: "",
    dateFrom: "",
    dateTo: "",
    visitType: "",
    status: "",
  });

  // Refs for date pickers
  const dateFromInputRef = useRef(null);
  const dateToInputRef = useRef(null);
  const dateFromPickerRef = useRef(null);
  const dateToPickerRef = useRef(null);

  // Initialize date pickers
  useEffect(() => {
    // Use a small delay to ensure refs are attached and DOM is ready
    const timeoutId = setTimeout(() => {
      if (dateFromInputRef.current && !dateFromPickerRef.current) {
        try {
          dateFromPickerRef.current = flatpickr(dateFromInputRef.current, {
            dateFormat: "Y-m-d",
            allowInput: false,
            clickOpens: true,
            static: false,
            appendTo: document.body,
            onChange: function (selectedDates, dateStr) {
              setFilters((prev) => ({ ...prev, dateFrom: dateStr }));
              setCurrentPage(1);
            },
          });
        } catch (error) {
          console.error('Error initializing date from picker:', error);
        }
      }
      if (dateToInputRef.current && !dateToPickerRef.current) {
        try {
          dateToPickerRef.current = flatpickr(dateToInputRef.current, {
            dateFormat: "Y-m-d",
            allowInput: false,
            clickOpens: true,
            static: false,
            appendTo: document.body,
            onChange: function (selectedDates, dateStr) {
              setFilters((prev) => ({ ...prev, dateTo: dateStr }));
              setCurrentPage(1);
            },
          });
        } catch (error) {
          console.error('Error initializing date to picker:', error);
        }
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (dateFromPickerRef.current) {
        try {
          dateFromPickerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying date from picker:', error);
        }
        dateFromPickerRef.current = null;
      }
      if (dateToPickerRef.current) {
        try {
          dateToPickerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying date to picker:', error);
        }
        dateToPickerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (clinicId) {
      fetchVisitReports();
    }
  }, [clinicId]);

  // Apply filters when filters or currentPage changes
  useEffect(() => {
    applyFilters();
  }, [filters, allVisits, currentPage]);

  const fetchVisitReports = async () => {
    if (!clinicId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch both inpatient and outpatient visits with completed/checkout/closed status
      // Make separate calls for each status and combine results
      const statuses = ["completed", "checkout", "closed"];
      
      const [inpatientPromises, outpatientPromises] = [
        statuses.map(status => 
          clinicAppointmentsAPI.searchInpatientVisits({
            clinic_id: clinicId,
            status: status,
            page: 1,
            per_page: 100, // Get more results per status
          })
        ),
        statuses.map(status => 
          clinicAppointmentsAPI.searchOutpatientVisits({
            clinic_id: clinicId,
            status: status,
            page: 1,
            per_page: 100, // Get more results per status
          })
        ),
      ];

      const [inpatientResults, outpatientResults] = await Promise.all([
        Promise.all(inpatientPromises),
        Promise.all(outpatientPromises),
      ]);

      // Combine all inpatient results - check the correct response structure
      const allInpatientVisits = inpatientResults.flatMap((response, index) => {
        try {
          if (response?.data?.status === "success") {
            const visits = response.data.data?.inpatient_visits || response.data.data || [];
            return Array.isArray(visits) ? visits : [];
          }
          console.warn(`Inpatient response ${index} not successful:`, response?.data);
          return [];
        } catch (err) {
          console.error(`Error processing inpatient response ${index}:`, err);
          return [];
        }
      });

      // Combine all outpatient results - check the correct response structure
      const allOutpatientVisits = outpatientResults.flatMap((response, index) => {
        try {
          if (response?.data?.status === "success") {
            const visits = response.data.data?.appointments || response.data.data || [];
            return Array.isArray(visits) ? visits : [];
          }
          console.warn(`Outpatient response ${index} not successful:`, response?.data);
          return [];
        } catch (err) {
          console.error(`Error processing outpatient response ${index}:`, err);
          return [];
        }
      });

      console.log('Visit Reports Debug:', {
        inpatientCount: allInpatientVisits.length,
        outpatientCount: allOutpatientVisits.length,
        sampleInpatient: allInpatientVisits[0],
        sampleOutpatient: allOutpatientVisits[0],
      });

      // Transform and combine the data
      const inpatientVisits = allInpatientVisits.map((visit) => ({
        id: visit.id,
        guardianName: visit.pet_owner
          ? `${visit.pet_owner.first_name || ""} ${visit.pet_owner.last_name || ""}`.trim()
          : "N/A",
        guardianPhone: visit.pet_owner?.phone || visit.pet_owner?.mobile || "",
        petName: visit.pet?.name || "N/A",
        date: visit.visit_date ? visit.visit_date.split(" ")[0] : "N/A",
        visitType: "Inpatient",
        status: visit.status || "N/A",
        originalData: visit,
      }));

      const outpatientVisits = allOutpatientVisits.map((visit) => ({
        id: visit.id,
        guardianName: visit.pet_owner
          ? `${visit.pet_owner.first_name || ""} ${visit.pet_owner.last_name || ""}`.trim()
          : "N/A",
        guardianPhone: visit.pet_owner?.phone || visit.pet_owner?.mobile || "",
        petName: visit.pet?.name || "N/A",
        date: visit.visit_date ? visit.visit_date.split(" ")[0] : "N/A",
        visitType: "Outpatient",
        status: visit.status || "N/A",
        originalData: visit,
      }));

      // Combine both types, remove duplicates, and sort by date (newest first)
      const combinedVisits = [...inpatientVisits, ...outpatientVisits]
        .filter((visit, index, self) => 
          index === self.findIndex(v => v.id === visit.id && v.visitType === visit.visitType)
        )
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Newest first
        });

      // Store all visits for filtering
      setAllVisits(combinedVisits);
    } catch (err) {
      console.error("Error fetching visit reports:", err);
      setError(t("visitReports.error", "Failed to load visit reports"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === "ar" ? "ar-SA" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatStatus = (status) => {
    if (!status) return "N/A";
    const statusLower = status.toLowerCase();
    if (statusLower === "checkout") return t("visitReports.status.checkout", "Checked Out");
    if (statusLower === "completed") return t("visitReports.status.completed", "Completed");
    if (statusLower === "closed") return t("visitReports.status.closed", "Closed");
    return status;
  };

  const hasPrescription = (visit) => {
    const prescription = visit.originalData?.prescription;
    return typeof prescription === "string" && prescription.trim().length > 0;
  };

  const handleViewDetails = (visit) => {
    if (visit.visitType === "Inpatient") {
      setSelectedInpatientId(visit.id);
      setShowInpatientModal(true);
    } else {
      setSelectedOutpatientId(visit.id);
      setShowOutpatientModal(true);
    }
  };

  const downloadBlob = (blob, defaultFileName) => {
    const disposition = blob.headers?.['content-disposition'];
    let fileName = defaultFileName;
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) {
        fileName = match[1];
      }
    }

    const blobUrl = URL.createObjectURL(blob.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleGenerateInvoice = async (visit) => {
    const invoiceKey = `${visit.visitType}-${visit.id}`;
    try {
      setGeneratingInvoiceId(invoiceKey);
      const response = await pdfServices.generateInvoice(
        visit.id,
        visit.visitType.toLowerCase()
      );

      downloadBlob(response, `invoice-${visit.visitType.toLowerCase()}-${visit.id}.pdf`);
    } catch (err) {
      console.error("Error generating invoice:", err);
      alert(t("visitReports.invoiceError", "Failed to generate invoice"));
    } finally {
      setGeneratingInvoiceId(null);
    }
  };

  const handleGeneratePrescriptionInvoice = async (visit) => {
    const prescriptionKey = `${visit.visitType}-${visit.id}`;
    try {
      setGeneratingPrescriptionId(prescriptionKey);
      const response = await pdfServices.generatePrescriptionInvoice(
        visit.id,
        visit.visitType.toLowerCase()
      );

      downloadBlob(response, `prescription-${visit.visitType.toLowerCase()}-${visit.id}.pdf`);
    } catch (err) {
      console.error("Error generating prescription PDF:", err);
      alert(t("visitReports.prescriptionError", "Failed to generate prescription PDF"));
    } finally {
      setGeneratingPrescriptionId(null);
    }
  };

  const handleCloseInpatientModal = () => {
    setShowInpatientModal(false);
    setSelectedInpatientId(null);
  };

  const handleCloseOutpatientModal = () => {
    setShowOutpatientModal(false);
    setSelectedOutpatientId(null);
  };

  // Apply filters to all visits
  const applyFilters = () => {
    let filtered = [...allVisits];

    // Filter by guardian name
    if (filters.guardianName) {
      filtered = filtered.filter((visit) =>
        visit.guardianName.toLowerCase().includes(filters.guardianName.toLowerCase())
      );
    }

    // Filter by guardian phone
    if (filters.guardianPhone) {
      filtered = filtered.filter((visit) => {
        const phone = visit.guardianPhone || visit.originalData?.pet_owner?.phone || visit.originalData?.pet_owner?.mobile || "";
        return phone.toLowerCase().includes(filters.guardianPhone.toLowerCase());
      });
    }

    // Filter by pet name
    if (filters.petName) {
      filtered = filtered.filter((visit) =>
        visit.petName.toLowerCase().includes(filters.petName.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((visit) => {
        if (visit.date === "N/A") return false;
        return visit.date >= filters.dateFrom;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter((visit) => {
        if (visit.date === "N/A") return false;
        return visit.date <= filters.dateTo;
      });
    }

    // Filter by visit type
    if (filters.visitType) {
      filtered = filtered.filter((visit) => visit.visitType === filters.visitType);
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((visit) => {
        const visitStatus = visit.status?.toLowerCase() || "";
        return visitStatus === filters.status.toLowerCase();
      });
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedVisits = filtered.slice(startIndex, endIndex);

    setVisits(paginatedVisits);
    setTotalPages(Math.ceil(filtered.length / perPage));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      guardianName: "",
      guardianPhone: "",
      petName: "",
      dateFrom: "",
      dateTo: "",
      visitType: "",
      status: "",
    });
    if (dateFromPickerRef.current) {
      dateFromPickerRef.current.clear();
    }
    if (dateToPickerRef.current) {
      dateToPickerRef.current.clear();
    }
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="visit-reports-container" dir={isRTL ? "rtl" : "ltr"}>
        <div className="visit-reports-loading">
          <div className="visit-reports-spinner"></div>
          <p>{t("visitReports.loading", "Loading visit reports...")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visit-reports-container" dir={isRTL ? "rtl" : "ltr"}>
        <div className="visit-reports-error">
          <p>{error}</p>
          <button onClick={fetchVisitReports} className="visit-reports-retry-btn">
            {t("visitReports.retry", "Retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visit-reports-container" dir={isRTL ? "rtl" : "ltr"}>
      <div className="visit-reports-header">
        <h1 className="visit-reports-title">
          {t("visitReports.title", "Visit Reports")}
        </h1>
        <p className="visit-reports-subtitle">
          {t("visitReports.subtitle", "View all completed and checked out visits")}
        </p>
      </div>

      {/* Filters Section */}
      <div className="visit-reports-filters">
        <div className="visit-reports-filters-row">
          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.guardianName", "Guardian Name")}
            </label>
            <input
              type="text"
              className="visit-reports-filter-input"
              value={filters.guardianName}
              onChange={(e) => handleFilterChange("guardianName", e.target.value)}
              placeholder={t("visitReports.filters.guardianNamePlaceholder", "Search by guardian name...")}
            />
          </div>

          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.guardianPhone", "Guardian Phone")}
            </label>
            <input
              type="text"
              className="visit-reports-filter-input"
              value={filters.guardianPhone}
              onChange={(e) => handleFilterChange("guardianPhone", e.target.value)}
              placeholder={t("visitReports.filters.guardianPhonePlaceholder", "Search by phone...")}
            />
          </div>

          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.petName", "Pet Name")}
            </label>
            <input
              type="text"
              className="visit-reports-filter-input"
              value={filters.petName}
              onChange={(e) => handleFilterChange("petName", e.target.value)}
              placeholder={t("visitReports.filters.petNamePlaceholder", "Search by pet name...")}
            />
          </div>

          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.visitType", "Visit Type")}
            </label>
            <select
              className="visit-reports-filter-select"
              value={filters.visitType}
              onChange={(e) => handleFilterChange("visitType", e.target.value)}
            >
              <option value="">{t("visitReports.filters.allTypes", "All Types")}</option>
              <option value="Inpatient">{t("visitReports.filters.inpatient", "Inpatient")}</option>
              <option value="Outpatient">{t("visitReports.filters.outpatient", "Outpatient")}</option>
            </select>
          </div>

          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.status", "Status")}
            </label>
            <select
              className="visit-reports-filter-select"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">{t("visitReports.filters.allStatuses", "All Statuses")}</option>
              <option value="completed">{t("visitReports.status.completed", "Completed")}</option>
              <option value="checkout">{t("visitReports.status.checkout", "Checked Out")}</option>
              <option value="closed">{t("visitReports.status.closed", "Closed")}</option>
            </select>
          </div>

          <div className="visit-reports-filter-group visit-reports-filter-actions">
            <label className="visit-reports-filter-label" style={{ visibility: 'hidden' }}>
              {t("visitReports.filters.status", "Status")}
            </label>
            <button
              onClick={handleClearFilters}
              className="visit-reports-clear-btn"
            >
              {t("visitReports.filters.clear", "Clear Filters")}
            </button>
          </div>
        </div>

        <div className="visit-reports-filters-row">
          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.dateFrom", "Date From")}
            </label>
            <input
              ref={dateFromInputRef}
              type="text"
              readOnly
              data-input
              className="visit-reports-filter-input visit-reports-date-input"
              placeholder={t("visitReports.filters.dateFromPlaceholder", "Select start date...")}
              onClick={() => {
                if (dateFromPickerRef.current) {
                  dateFromPickerRef.current.open();
                }
              }}
            />
          </div>

          <div className="visit-reports-filter-group">
            <label className="visit-reports-filter-label">
              {t("visitReports.filters.dateTo", "Date To")}
            </label>
            <input
              ref={dateToInputRef}
              type="text"
              readOnly
              data-input
              className="visit-reports-filter-input visit-reports-date-input"
              placeholder={t("visitReports.filters.dateToPlaceholder", "Select end date...")}
              onClick={() => {
                if (dateToPickerRef.current) {
                  dateToPickerRef.current.open();
                }
              }}
            />
          </div>
        </div>
      </div>

      {visits.length === 0 && !loading ? (
        <div className="visit-reports-empty">
          <p>{t("visitReports.noVisits", "No visit reports found")}</p>
        </div>
      ) : (
        <>
          <div className="visit-reports-table-wrapper">
            <table className="visit-reports-table">
              <thead>
                <tr>
                  <th>{t("visitReports.table.guardian", "Guardian Name")}</th>
                  <th>{t("visitReports.table.pet", "Pet Name")}</th>
                  <th>{t("visitReports.table.date", "Date")}</th>
                  <th>{t("visitReports.table.visitType", "Visit Type")}</th>
                  <th>{t("visitReports.table.status", "Status")}</th>
                  <th>{t("visitReports.table.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={`${visit.visitType}-${visit.id}`}>
                    <td>{visit.guardianName}</td>
                    <td>{visit.petName}</td>
                    <td>{formatDate(visit.date)}</td>
                    <td>
                      <span className={`visit-type-badge visit-type-${visit.visitType.toLowerCase()}`}>
                        {visit.visitType}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${visit.status.toLowerCase()}`}>
                        {formatStatus(visit.status)}
                      </span>
                    </td>
                    <td>
                      <div className="visit-reports-actions">
                        <button
                          onClick={() => handleViewDetails(visit)}
                          className="visit-reports-view-btn"
                          title={t("visitReports.viewDetails", "View Details")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          {t("visitReports.viewDetails", "View Details")}
                        </button>
                        {visit.status?.toLowerCase() === "completed" && (
                          <>
                            <button
                              onClick={() => handleGenerateInvoice(visit)}
                              disabled={generatingInvoiceId === `${visit.visitType}-${visit.id}`}
                              className="visit-reports-invoice-btn"
                              title={t("visitReports.generateInvoice", "Generate Invoice")}
                            >
                              {generatingInvoiceId === `${visit.visitType}-${visit.id}` ? (
                                <span className="visit-reports-invoice-spinner" />
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                  <polyline points="14 2 14 8 20 8"></polyline>
                                  <line x1="16" y1="13" x2="8" y2="13"></line>
                                  <line x1="16" y1="17" x2="8" y2="17"></line>
                                  <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                              )}
                              {t("visitReports.invoice", "Invoice")}
                            </button>
                            {hasPrescription(visit) && (
                              <button
                                onClick={() => handleGeneratePrescriptionInvoice(visit)}
                                disabled={generatingPrescriptionId === `${visit.visitType}-${visit.id}`}
                                className="visit-reports-prescription-btn"
                                title={t("visitReports.generatePrescription", "Generate Prescription")}
                              >
                                {generatingPrescriptionId === `${visit.visitType}-${visit.id}` ? (
                                  <span className="visit-reports-invoice-spinner" />
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 3h6"></path>
                                    <path d="M10 3v5.5a4 4 0 1 0 4 0V3"></path>
                                    <path d="M8 14h8"></path>
                                    <path d="M12 18v3"></path>
                                  </svg>
                                )}
                                {t("visitReports.prescription", "Prescription")}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="visit-reports-pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="visit-reports-pagination-btn"
              >
                {t("visitReports.pagination.previous", "Previous")}
              </button>
              <span className="visit-reports-pagination-info">
                {t("visitReports.pagination.page", "Page")} {currentPage} {t("visitReports.pagination.of", "of")} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="visit-reports-pagination-btn"
              >
                {t("visitReports.pagination.next", "Next")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Inpatient Details Modal */}
      {showInpatientModal && selectedInpatientId && (
        <InpatientDetailsModal
          isOpen={showInpatientModal}
          inpatientId={selectedInpatientId}
          onClose={handleCloseInpatientModal}
          workingHours={[]}
        />
      )}

      {/* Outpatient Details Overlay */}
      {showOutpatientModal && selectedOutpatientId && (
        <OutpatientDetailsOverlay
          isVisible={showOutpatientModal}
          outpatientId={selectedOutpatientId}
          onClose={handleCloseOutpatientModal}
          workingHours={[]}
        />
      )}
    </div>
  );
};

export default VisitReports;
